import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Star, Heart, Share2, MessageCircle, 
  Phone, Shield, Calendar, CheckCircle, Package, Truck, 
  Flag, ChevronRight, Check, X, Store,
  Lock, CreditCard, AlertCircle, Handshake, Users
} from 'lucide-react';
import { startConversation, getConversations, getListing,getListings, getSellerStats, getListingRatings } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';
import Loading from '../../components/common/Loading';
import ChatModal from '../../components/listing/ChatModal';
import RatingsSummary from '../../components/listing/RatingsSummary';
import ReviewItem from '../../components/listing/ReviewItem';
import ImageModal from '../../components/listing/ImageModal';
import ReportModal from '../../components/listing/ReportModal';

// Import ProductGrid for related items
import ProductGrid from '../home/ProductGrid';

// Import specialized detail components
import ElectronicsDetails from '../../components/listing/ElectronicsDetails';
import PropertiesDetails from '../../components/listing/PropertiesDetails';
import ServicesDetails from '../../components/listing/ServicesDetails';
import VehiclesDetails from '../../components/listing/VehiclesDetails';
import DefaultDetails from '../../components/listing/DefaultDetails';

// Import shared utilities
import { formatPrice, getCategoryType, shouldShowCondition } from '../../utils/listingUtils';

// Import Header and Footer from same path
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [existingConversation, setExistingConversation] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [ratingStats, setRatingStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showAllReviews, setShowAllReviews] = useState(false);
  
  // State for related items
  const [relatedListings, setRelatedListings] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  useEffect(() => {
    if (listing) {
      fetchRatings();
      fetchRelatedItems();
    }
  }, [listing]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      // console.log('Loading listing ID:', id);
      
      const listingData = await getListing(id);
      // console.log('Listing response:', listingData);
      
      // Direct access to listing data
      if (listingData && listingData.data) {
        setListing(listingData.data);
      } else if (listingData) {
        // If API returns listing directly without .data wrapper
        setListing(listingData);
      } else {
        setError('Listing not found');
      }
    } catch (error) {
      // console.error('Fetch error:', error);
      setError(error.message || 'Failed to load listing');
      setListing(null); // Clear listing on error
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    if (!listing) return;
    
    setReviewsLoading(true);
    try {
      // Fetch seller rating stats
      if (listing.seller_id) {
        const statsResponse = await getSellerStats(listing.seller_id);
        if (statsResponse && statsResponse.data) {
          setRatingStats(statsResponse.data);
        }
      }
      
      // Fetch listing-specific ratings
      const reviewsResponse = await getListingRatings(listing.listing_id);
      if (reviewsResponse && reviewsResponse.data) {
        // Filter only approved ratings
        const approvedReviews = reviewsResponse.data.filter(
          review => review.status === 'approved'
        );
        setReviews(approvedReviews);
      }
    } catch (error) {
      // console.error('Error fetching ratings:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Fetch related items based on category
  const fetchRelatedItems = async () => {
    if (!listing) return;
    
    setRelatedLoading(true);
    try {
      // Use the same API function but with category filter
      const relatedData = await getListings({ 
        category_id: listing.category_id,
        status: 'active',
        limit: 12, // Show 12 related items
        exclude: listing.listing_id // Exclude current listing
      });
      
      if (relatedData && relatedData.data) {
        setRelatedListings(relatedData.data);
      } else if (Array.isArray(relatedData)) {
        setRelatedListings(relatedData.slice(0, 12));
      }
    } catch (error) {
      // console.error('Error fetching related items:', error);
      setRelatedListings([]);
    } finally {
      setRelatedLoading(false);
    }
  };

  const handleContactSeller = async () => {
    if (!user) {
      // Redirect to login or show login modal
      navigate('/login', { state: { from: `/listings/${id}` } });
      return;
    }
  
    if (!listing || !listing.seller_id) {
      alert('Unable to contact seller. Please try again.');
      return;
    }
  
    // Check if user is trying to contact themselves
    if (user.user_id === listing.seller_id) {
      alert("You can't contact yourself!");
      return;
    }
  
    setChatLoading(true);
  
    try {
      // First, check if conversation already exists
      const conversationsData = await getConversations(user.user_id);
      const conversations = conversationsData.data || conversationsData || [];
      
      // Find conversation with this seller and listing
      const existingConv = conversations.find(conv => 
        conv.seller_id === listing.seller_id && 
        conv.listing_id === parseInt(id)
      );
  
      if (existingConv) {
        // Conversation exists, open chat with existing conversation
        setExistingConversation(existingConv);
        setShowChatModal(true);
      } else {
        // Create new conversation
        const conversationData = {
          buyer_id: user.user_id,
          seller_id: listing.seller_id,
          listing_id: parseInt(id),
          initial_message: `Hi, I'm interested in your listing "${listing.title}"`
        };
  
        const newConversationData = await startConversation(conversationData);
        
        // Direct access to conversation data
        if (newConversationData && newConversationData.data) {
          setExistingConversation(newConversationData.data);
        } else if (newConversationData) {
          setExistingConversation(newConversationData);
        } else {
          throw new Error('No conversation data returned');
        }
        
        setShowChatModal(true);
      }
    } catch (error) {
      // console.error('Error starting conversation:', error);
      alert('Failed to contact seller. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleShare = () => {
    if (!listing) return;

    const shareUrl = `${window.location.origin}/listings/${listing.listing_id}`;
    const shareMessage = `Check out this listing on EcoMart:\n\n` +
                       `🏷️ ${listing.title}\n` +
                       `💰 ${formatPrice(listing.price)}\n` +
                       `📍 ${listing.state_name || 'Location not specified'}\n` +
                       `\n${shareUrl}`;

    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: shareMessage,
        url: shareUrl,
      })
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl)
        .then(() => alert('Link copied to clipboard!'))
        .catch(err => {
          // console.log('Clipboard error:', err);
          alert('Failed to copy link');
        });
    }
  };

  const handleToggleFavorite = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setIsFavorited(!isFavorited);
    // TODO: Call API to add/remove from favorites
  };

  const renderSpecializedDetails = () => {
    if (!listing || !listing.category_id) return null;

    const categoryType = getCategoryType(listing.category_id);
    
    switch(categoryType) {
      case 'electronics':
        return <ElectronicsDetails listing={listing} />;
      case 'properties':
        return <PropertiesDetails listing={listing} />;
      case 'services':
        return <ServicesDetails listing={listing} />;
      case 'vehicles':
        return <VehiclesDetails listing={listing} />;
      default:
        return <DefaultDetails listing={listing} />;
    }
  };

  const getCategoryDisplayName = () => {
    if (!listing) return '';
    
    if (listing.subcategory_name) {
      return `${listing.category_name} › ${listing.subcategory_name}`;
    }
    
    return listing.category_name || '';
  };

  const handleViewAllReviews = () => {
    if (reviews.length > 0) {
      navigate(`/listings/${listing.listing_id}/reviews`);
    }
  };

  const handleWriteReview = () => {
    if (!user) {
      alert('Login Required', 'Please log in to write a review');
      return;
    }
    
    alert(
      'Write a Review',
      'You can review items you\'ve purchased from your orders page.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Go to Orders', onPress: () => navigate('/orders') }
      ]
    );
  };

  const handleViewSellerProfile = () => {
    if (listing && listing.seller_id) {
      navigate(`/seller/${listing.seller_id}`);
    }
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setShowImageModal(true);
  };

  // Helper function to format review data
  const formatReviewData = (review) => {
    return {
      ...review,
      rating_overall: review.rating_overall || 0,
      rating_quality: null, // We're not using detailed ratings
      rating_communication: null,
      rating_delivery: null,
      review_text: review.review_text || '',
      buyer_name: review.buyer_first_name && review.buyer_last_name 
        ? `${review.buyer_first_name} ${review.buyer_last_name}`
        : review.buyer_name || 'Anonymous',
      buyer_profile_picture: review.buyer_profile_picture || '',
      is_verified_purchase: false, // Most are chat-based, not verified purchases
      created_at: review.created_at,
      status: review.status || 'approved'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading listing..." />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Listing Not Found</h2>
          <p className="text-gray-600 mb-6 max-w-md">{error || 'The listing you are looking for does not exist or has been removed.'}</p>
          <div className="flex space-x-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-[#ff660e] text-white px-6 py-2 rounded-lg hover:bg-[#ff5500]"
            >
              Browse Listings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const images = listing.images || [];
  const seller = listing.seller || {};
  const categoryType = getCategoryType(listing.category_id);
  
  // Get average rating
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + (r.rating_overall || 0), 0) / reviews.length).toFixed(1)
    : 0;

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium text-black">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={handleToggleFavorite}
                className={`p-2 rounded-full transition-colors ${
                  isFavorited 
                    ? 'text-red-500 hover:bg-red-50' 
                    : 'text-gray-600 hover:text-[#ff660e] hover:bg-orange-50'
                }`}
              >
                <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
              </button>
              <button 
                onClick={handleShare}
                className="p-2 text-gray-600 hover:text-[#ff660e] rounded-full hover:bg-orange-50"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pb-24"> {/* Extra padding for bottom action bar */}
        {/* Main Image Section */}
        <div className="relative">
          {images.length > 0 ? (
            <div className="relative">
              <img
                src={getImageUrl(images[currentImageIndex].image_url)}
                alt={listing.title}
                className="w-full h-64 object-cover bg-gray-100"
                onClick={() => handleImageClick(currentImageIndex)}
              />
              {listing.is_boosted && (
                <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-[#ff660e] text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg">
                  <span>⚡</span> BOOSTED
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-64 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">📦</div>
                <p className="text-gray-500">No image available</p>
              </div>
            </div>
          )}
        </div>

        {/* Image Thumbnails */}
        {images.length > 1 && (
          <div className="px-4 py-3 overflow-x-auto">
            <div className="flex space-x-3">
              {images.map((image, index) => (
                <button
                  key={image.image_id}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                    index === currentImageIndex 
                      ? 'border-[#ff660e] shadow-sm' 
                      : 'border-gray-200'
                  }`}
                >
                  <img
                    src={getImageUrl(image.image_url)}
                    alt={`${listing.title} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Listing Info Card */}
        <div className="bg-white mx-4 mt-4 rounded-xl shadow-sm p-4">
          <h1 className="text-xl font-bold text-black mb-2">{listing.title}</h1>
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-2xl font-bold text-[#ff660e]">{formatPrice(listing.price)}</p>
              <p className="text-sm text-gray-500">{listing.currency || 'NGN'}</p>
            </div>
            
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {listing.state_name}
              </span>
            </div>
          </div>

          {/* Category Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {listing.category_name}
            </span>
            {listing.is_negotiable === 1 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <span className="mr-1">💬</span> Negotiable
              </span>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 py-3 border-t border-gray-100">
            {shouldShowCondition(listing.category_id) && listing.condition && (
              <div className="text-center">
                <p className="text-xs text-gray-500">Condition</p>
                <p className="text-sm font-semibold capitalize">{listing.condition}</p>
              </div>
            )}
            <div className="text-center">
              <p className="text-xs text-gray-500">Views</p>
              <p className="text-sm font-semibold">{listing.view_count || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Listed</p>
              <p className="text-sm font-semibold">
                {new Date(listing.created_at).toLocaleDateString('en-NG', {
                  day: 'numeric',
                  month: 'short'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Seller Info Card */}
        <div className="bg-white mx-4 mt-4 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-black">Seller Information</h2>
            <div className="flex items-center text-yellow-600">
              <Star className="w-4 h-4 fill-current" />
              <span className="ml-1 font-semibold">{seller.rating_avg || averageRating || '0.0'}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {seller.store_logo_url ? (
              <img
                src={getImageUrl(seller.store_logo_url)}
                alt={`${seller.first_name} ${seller.last_name}`}
                className="w-14 h-14 rounded-full border-2 border-white shadow-sm object-cover"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#ff660e] to-red-500 flex items-center justify-center text-white font-bold text-xl shadow-sm">
                {seller.first_name?.[0] || 'S'}
              </div>
            )}
            
            <div className="flex-1">
              <h3 className="font-bold text-black">{seller.store_name}</h3>
              {seller.store_name && (
                <p className="text-sm text-gray-700">by {seller.first_name} {seller.last_name}</p>
              )}
              {/* <p className="text-xs text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {seller.state_name}
              </p> */}
            </div>
            
            <button
              onClick={handleViewSellerProfile}
              className="text-[#ff660e] text-sm font-semibold"
            >
              View
            </button>
          </div>
        </div>

        {/* Description Card */}
        <div className="bg-white mx-4 mt-4 rounded-xl shadow-sm p-4">
          <h2 className="font-bold text-black mb-3">Description</h2>
          <p className="text-gray-700 text-sm leading-relaxed">
            {listing.description || 'No description provided.'}
          </p>
        </div>

        {/* Specialized Details */}
        <div className="bg-white mx-4 mt-4 rounded-xl shadow-sm p-4">
          {renderSpecializedDetails()}
        </div>

        {/* Reviews Card */}
        <div className="bg-white mx-4 mt-4 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-black">Ratings & Reviews</h2>
            {reviews.length > 0 && (
              <button
                onClick={handleViewAllReviews}
                className="text-sm text-[#ff660e] font-medium"
              >
                View all ({reviews.length})
              </button>
            )}
          </div>
          
          {reviewsLoading ? (
            <div className="py-4">
              <p className="text-center text-gray-600">Loading reviews...</p>
            </div>
          ) : reviews.length > 0 ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center">
                  <Star className="w-5 h-5 fill-current text-yellow-400" />
                  <span className="text-xl font-bold ml-2">{averageRating}</span>
                  <span className="text-gray-500 ml-1">/5</span>
                </div>
                <div>
                  <p className="text-gray-700 text-sm">
                    Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              
              {/* Show first review */}
              {reviews[0] && (
                <ReviewItem review={formatReviewData(reviews[0])} />
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <Star className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">No reviews yet</p>
            </div>
          )}
        </div>

        {/* Safety Tips Card - Updated for EcoMart */}
        <div className="bg-gradient-to-r from-[#ff660e] to-orange-500 mx-4 mt-4 rounded-xl p-4 text-white">
          <div className="flex items-center mb-3">
            <Shield className="w-5 h-5 mr-2" />
            <h3 className="font-bold text-sm">Safe Trading Tips</h3>
          </div>
          <ul className="space-y-2 text-xs text-blue-100">
            <li className="flex items-start">
              <CheckCircle className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0" />
              <span>Always use EcoMart Escrow for secure payments</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0" />
              <span>Meet in safe public locations for exchanges</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0" />
              <span>Verify item condition before releasing funds</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0" />
              <span>Report suspicious activity immediately</span>
            </li>
          </ul>
          
          <div className="mt-4 pt-3 border-t border-blue-400">
            <h4 className="font-bold text-white text-xs mb-2 flex items-center">
              <Lock className="w-3 h-3 mr-1" />
              Why Use EcoMart Escrow?
            </h4>
            <p className="text-blue-100 text-xs">
              Your payment is held securely until you confirm receipt of the item as described.
            </p>
          </div>
        </div>

        {/* Report Button */}
        <button 
  onClick={() => setShowReportModal(true)}
  className="w-[calc(100%-32px)] ml-4 mt-4 flex items-center justify-center gap-2 p-3 bg-white rounded-xl shadow-sm text-gray-600 hover:text-red-600"
>
  <Flag className="w-4 h-4" />
  <span className="text-sm font-medium">Report this listing</span>
</button>

        {/* Related Items (Mobile) using ProductGrid */}
        <div className="mt-6 px-4">
          <h2 className="text-xl font-bold text-black mb-4">Related Items</h2>
          {relatedLoading ? (
            <div className="flex justify-center py-8">
              <Loading text="Loading related items..." />
            </div>
          ) : relatedListings.length > 0 ? (
            <ProductGrid
              listings={relatedListings.slice(0, 2)} // Show only 2 on mobile
              viewMode="grid"
              sortBy="newest"
              showControls={false}
              hidePagination={true}
              isSearchPage={false}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No related items found</p>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 z-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-lg font-bold text-black">{formatPrice(listing.price)}</p>
          </div>
          <div className="flex gap-2">
            {seller.phone_number && (
              <a 
                href={`tel:${seller.phone_number}`}
                className="flex items-center justify-center gap-1 px-3 py-2.5 border-2 border-[#ff660e] text-[#ff660e] rounded-lg text-sm font-semibold min-w-[100px]"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            <button 
              onClick={handleContactSeller}
              disabled={chatLoading}
              className="flex items-center justify-center gap-1 px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-red-500 text-white rounded-lg font-semibold text-sm min-w-[140px] disabled:opacity-50"
            >
              {chatLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Connecting
                </>
              ) : (
                <>
                  <MessageCircle className="w-4 h-4" />
                  Contact Seller
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Fixed Desktop Layout */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Images & Quick Stats */}
          <div className="lg:w-2/3 space-y-6">
            {/* Images Section */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {images.length > 0 ? (
                <div className="relative">
                  <img
                    src={getImageUrl(images[currentImageIndex].image_url)}
                    alt={listing.title}
                    className="w-full h-96 object-contain bg-gray-100 cursor-pointer"
                    onClick={() => handleImageClick(currentImageIndex)}
                  />
                  {listing.is_boosted && (
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-[#ff660e] text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg">
                      <span>⚡</span> BOOSTED
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📦</div>
                    <p className="text-gray-500 text-lg">No image available</p>
                  </div>
                </div>
              )}
              
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="p-4 border-t border-gray-100">
                  <div className="flex space-x-3 overflow-x-auto pb-2">
                    {images.map((image, index) => (
                      <button
                        key={image.image_id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          index === currentImageIndex 
                            ? 'border-[#ff660e] shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={getImageUrl(image.image_url)}
                          alt={`${listing.title} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Title and Price */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h1 className="text-2xl lg:text-3xl font-bold text-black mb-3">
                    {listing.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {listing.category_name}
                    </span>
                    {listing.is_negotiable === 1 && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        <span className="mr-1">💬</span> Negotiable
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-3xl lg:text-4xl font-bold text-[#ff660e]">
                    {formatPrice(listing.price)}
                  </p>
                  <p className="text-gray-500">{listing.currency || 'NGN'}</p>
                </div>
              </div>

              {/* Location */}
              <div className="mb-6">
                <div className="flex items-center text-gray-700">
                  <MapPin className="w-5 h-5 mr-2 text-gray-500" />
                  <span className="text-sm">
                    {listing.lcda_name && `${listing.lcda_name}, `}
                    {listing.lga_name && `${listing.lga_name}, `}
                    {listing.state_name}
                  </span>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                {shouldShowCondition(listing.category_id) && listing.condition && (
                  <div>
                    <p className="text-sm text-gray-500">Condition</p>
                    <p className="font-semibold capitalize">{listing.condition}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Views</p>
                  <p className="font-semibold">{listing.view_count || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Listed</p>
                  <p className="font-semibold">
                    {new Date(listing.created_at).toLocaleDateString('en-NG', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-semibold">{getCategoryDisplayName()}</p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-black mb-3">Description</h2>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {listing.description || 'No description provided.'}
                </p>
              </div>

              {/* Specialized Details */}
              <div className="mb-6">
                {renderSpecializedDetails()}
              </div>

              {/* Reviews Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-black">Ratings & Reviews</h2>
                  {reviews.length > 0 && (
                    <button
                      onClick={handleViewAllReviews}
                      className="text-sm text-[#ff660e] hover:text-[#ff5500] font-medium flex items-center gap-1"
                    >
                      View all ({reviews.length})
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {reviewsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loading text="Loading reviews..." />
                  </div>
                ) : reviews.length > 0 ? (
                  <>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex items-center">
                        <Star className="w-6 h-6 fill-current text-yellow-400" />
                        <span className="text-2xl font-bold ml-2">{averageRating}</span>
                        <span className="text-gray-500 ml-1">/5</span>
                      </div>
                      <div>
                        <p className="text-gray-700">
                          Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    
                    {/* Recent Reviews */}
                    <div className="space-y-4">
                      {reviews.slice(0, 2).map((review) => (
                        <ReviewItem key={review.rating_id || review.id} review={formatReviewData(review)} />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-black mb-2">No reviews yet</h3>
                    <p className="text-gray-600 mb-4">
                      Be the first to review this product
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Seller Info & Actions */}
          <div className="lg:w-1/3 space-y-6">
            {/* Action Buttons Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 top-24">
              <div className="space-y-4">
                <button 
                  onClick={handleContactSeller}
                  disabled={chatLoading}
                  className="w-full bg-gradient-to-r from-[#ff660e] to-red-500 text-white py-4 rounded-xl font-semibold hover:from-[#ff5500] hover:to-red-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {chatLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5" />
                      Contact Seller
                    </>
                  )}
                </button>
                
                {seller.phone_number && (
                  <a 
                    href={`tel:${seller.phone_number}`}
                    className="w-full border-2 border-[#ff660e] text-[#ff660e] py-4 rounded-xl font-semibold hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call Seller
                  </a>
                )}
                
                <div className="flex gap-2">
                  <button 
                    onClick={handleToggleFavorite}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-colors ${
                      isFavorited 
                        ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
                    {isFavorited ? 'Saved' : 'Save'}
                  </button>
                  <button 
                    onClick={handleShare}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                    Share
                  </button>
                </div>
              </div>

              {/* Report Button */}
              <button 
                onClick={() => setShowReportModal(true)}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl font-medium transition-colors"
              >
                <Flag className="w-5 h-5" />
                Report Listing
              </button>
            </div>

            {/* Seller Info Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-black">Seller Information</h2>
                <div className="flex items-center">
                  <Star className="w-5 h-5 fill-current text-yellow-400" />
                  <span className="ml-1 font-semibold">{seller.rating_avg || averageRating || '0.0'}</span>
                </div>
              </div>

              <div 
                className="flex items-center gap-4 cursor-pointer mb-4"
                onClick={handleViewSellerProfile}
              >
                <div className="relative">
                  {seller.store_logo_url ? (
                    <img
                      src={getImageUrl(seller.store_logo_url)}
                      alt={`${seller.first_name} ${seller.last_name}`}
                      className="w-16 h-16 rounded-full border-2 border-white shadow-sm object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#ff660e] to-red-500 flex items-center justify-center text-white font-bold text-xl shadow-sm">
                      {seller.first_name?.[0] || 'S'}
                    </div>
                  )}
                  {/* {seller.is_email_verified && (
                    <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                    </div>
                  )} */}
                </div>
                
                <div className="flex-1">
                  <h3 className="font-bold text-black hover:text-[#ff660e] transition-colors">
                  {seller.store_name}
                  </h3>
                  {seller.store_name && (
                    <p className="text-sm text-gray-700">by {seller.first_name} {seller.last_name}</p>
                  )}
                  {/* <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {listing.state_name}
                  </p> */}
                </div>
              </div>

              {/* Store Description */}
              {seller.store_description && (
                <div className="mb-4">
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {seller.store_description}
                  </p>
                </div>
              )}
              
              {/* View Profile Button */}
              <button
                onClick={handleViewSellerProfile}
                className="w-full bg-orange-50 text-[#ff660e] hover:bg-orange-100 font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Store className="w-4 h-4" />
                View Seller Profile
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Safety Tips Card */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 text-white">
              <div className="flex items-center mb-4">
                <Shield className="w-6 h-6 mr-3" />
                <h3 className="text-lg font-bold">Safe Trading Tips</h3>
              </div>
              <ul className="space-y-3 text-sm text-blue-100">
                <li className="flex items-start">
                  <Lock className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Use EcoMart Escrow for secure payments</span>
                </li>
                <li className="flex items-start">
                  <Package className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Inspect items before releasing funds</span>
                </li>
                <li className="flex items-start">
                  <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Report suspicious listings immediately</span>
                </li>
                <li className="flex items-start">
                  <Users className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Meet in public locations for exchanges</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Related Items Section using ProductGrid */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-black">Related Items</h2>
            {relatedListings.length > 0 && (
              <button 
                onClick={() => navigate(`/category/${listing.category_id}`)}
                className="text-[#ff660e] hover:text-[#ff5500] font-medium flex items-center gap-1"
              >
                View all in {listing.category_name}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {relatedLoading ? (
            <div className="flex justify-center py-12">
              <Loading text="Loading related items..." />
            </div>
          ) : relatedListings.length > 0 ? (
            <ProductGrid
              listings={relatedListings}
              viewMode="grid"
              sortBy="newest"
              currentPage={1}
              showControls={false}
              hidePagination={true}
              isSearchPage={false}
            />
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No related items found</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Browse other items in {listing.category_name}
              </p>
              <button
                onClick={() => navigate(`/category/${listing.category_id}`)}
                className="bg-[#ff660e] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#ff5500]"
              >
                Browse {listing.category_name}
              </button>
            </div>
          )}
        </div>
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />

      {/* Chat Modal */}
      <ChatModal
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        conversation={existingConversation}
        listing={listing}
        seller={seller}
        currentUser={user}
      />

      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal}
        images={images}
        selectedIndex={currentImageIndex}
        onClose={() => setShowImageModal(false)}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        listing={listing}
        onClose={() => setShowReportModal(false)}
      />
    </>
  );
}